import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCallback } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { HotelInfoItemInput } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import DragIndicator from "@/components/ui/icon/drag-indicator";

import { QuestionCardItem } from "../../../../../../_shared/ui/components/system/question/card/card-item";

type Props = {
	sortableId: string;
	index: number;
	item: ForAutoGenerator<HotelInfoItemInput, "">;

	removeHotelInfo: (index: number) => void;
	changeHotelInfoTitle: (value: string, index: number) => void;
	changeHotelInfoDescription: (value: string, index: number) => void;
};

export const SortableHotelInfoRow = ({
	sortableId,
	index,
	item,
	removeHotelInfo,
	changeHotelInfoTitle,
	changeHotelInfoDescription,
}: Props) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id: sortableId,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const handleRemoveItem = useCallback(() => {
		removeHotelInfo(index);
	}, [removeHotelInfo, index]);

	return (
		<QuestionCardItem
			removeItem={handleRemoveItem}
			ref={setNodeRef}
			style={style}
			className={cn(isDragging && "!cursor-grabbing")}
		>
			<div
				ref={setActivatorNodeRef}
				className="absolute left-4 top-4"
				aria-label="注意事項の並べ替え"
				{...attributes}
				{...listeners}
			>
				<DragIndicator
					className={cn(
						"text-caption-50 hover:cursor-grab",
						isDragging ? "!cursor-grabbing" : "cursor-grab",
					)}
					size="default"
				/>
			</div>
			<div className="flex flex-col gap-y-10 pl-10">
				<InputTextField
					label="タイトル"
					value={item.title.value}
					handleChange={(input) => changeHotelInfoTitle(input, index)}
					placeholder="例：施設は禁煙ですか？"
					errorMessage={item.title.error}
				/>
				<TextAreaField
					label="説明"
					value={item.description.value}
					handleChange={(input) => changeHotelInfoDescription(input, index)}
					placeholder="例：全室禁煙となっていますが、喫煙ルームをご用意しております。"
					errorMessage={item.description.error}
				/>
			</div>
		</QuestionCardItem>
	);
};

SortableHotelInfoRow.displayName = "SortableHotelInfoRow";
